<template>
  <div class="meters">
    <v-row>
      <v-col>
        <breadcrumb :items="breadcrumbs" />
      </v-col>
      <v-col class="text-right">
        <v-btn
          v-if="can('main-meter-create')"
          color="primary"
          class="mb-5"
          @click="showAddMeterDialog = true"
        >
          <v-icon
            left
            size="22"
          >
            {{ icons.mdiTableLargePlus }}
          </v-icon>
          Add Main Meter
        </v-btn>
      </v-col>
    </v-row>
    <v-card>
      <v-card-title>
        Main Meters
        <v-spacer></v-spacer>
        <v-col class="col-md-4">
          <search-input v-model="search" />
        </v-col>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="meters"
        :loading="isMetersLoading"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        hide-default-footer
      >
        <template v-slot:progress>
          <v-progress-linear
            indeterminate
            class="mt-2"
            rounded
          ></v-progress-linear>
          <p class="text-center mt-1">
            Loading...Please wait
          </p>
        </template>
        <template v-slot:body="{ items }">
          <tbody v-if="meters.length > 0">
            <tr
              v-for="meter in items"
              :key="meter.id"
              style="cursor: pointer"
              @click="onMeterClick(meter.id)"
            >
              <td>{{ meter.number }}</td>
              <td>{{ meter.station.name }}</td>
              <td>
                <span v-if="meter.mode == 1">{{ meter.type.name }}</span>
                <span v-else>Manual</span>
              </td>
              <td
                style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                :title="meter.description"
              >{{ meter.description }}</td>
              <td
                v-if="can('main-meter-edit') || can('main-meter-delete')"
                @click.stop
              >
                <v-tooltip
                  v-if="can('main-meter-edit')"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="editMeter(meter)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiPencil }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip
                  v-if="can('main-meter-delete')"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="showDeleteMeterDialog(meter.id)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiDelete }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-show="!isMetersLoading">
              <td
                :colspan="headers.length"
                style="text-align: center"
              >
                No meters found
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-divider />
      <v-pagination
        v-model="pagination.page"
        class="mt-1"
        :length="pagination.pageCount"
        :total-visible="8"
        @input="onPageChange"
      ></v-pagination>
    </v-card>
    <add-main-meter-dialog
      :show-dialog="showAddMeterDialog"
      :meter="meter"
      @close="
        showAddMeterDialog = false
        meter = {}
      "
      @meter-added="getMeters()"
    />
    <confirm-dialog
      :show-dialog="showConfirmDeleteDialog"
      :is-agree-button-loading="isConfirmDeleteDialogButtonLoading"
      :agree-text="'Delete'"
      @cancel="showConfirmDeleteDialog = false"
      @agree="deleteMeter()"
    />
  </div>
</template>
<script>
import axios from 'axios'
import _ from 'lodash'
import {
  mdiMagnify, mdiPencil, mdiDelete, mdiTableLargePlus,
} from '@mdi/js'
import hasPermission from '@/mixins/hasPermission'
import AddMainMeterDialog from '@/components/dialogs/AddMainMeterDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import BreadCrumb from '@/components/partials/BreadCrumb.vue'
import SearchInput from '@/components/partials/SearchInput.vue'

export default {
  components: {
    AddMainMeterDialog,
    ConfirmDialog,
    breadcrumb: BreadCrumb,
    SearchInput,
  },
  mixins: [hasPermission],
  data() {
    return {
      showAddMeterDialog: false,
      showConfirmDeleteDialog: false,
      isConfirmDeleteDialogButtonLoading: false,
      toDelete: null,
      sortBy: ['created_at'],
      sortDesc: [true],
      search: '',
      pagination: {
        page: 1,
        pageCount: 1,
        itemsPerPage: 10,
      },
      headers: [
        {
          text: 'Number',
          align: 'start',
          value: 'number',
          sortable: false,
        },
        {
          text: 'Station',
          value: 'station',
          sortable: false,
        },
        {
          text: 'Type',
          value: 'type',
          sortable: false,
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false,
        },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      icons: {
        mdiMagnify,
        mdiPencil,
        mdiDelete,
        mdiTableLargePlus,
      },
      meter: {},
      meterId: this.$route.params.id,
      meters: [],
      isMetersLoading: true,
      metersUrl: 'meters',
      breadcrumbs: [
        {
          text: 'Dashboard',
          disabled: false,
          to: { name: 'dashboard' },
        },
        {
          text: 'Main Meters',
          disabled: false,
          to: { name: 'main-meters' },
        },
      ],
    }
  },
  computed: {
    sortOrder() {
      return this.sortDesc[0] ? 'desc' : 'asc'
    },
    refreshTable() {
      return `${this.userUrl}|${this.search}|${this.sortBy}|${this.sortDesc}`
    },
  },
  watch: {
    refreshTable() {
      this.getMeters()
    },
  },
  mounted() {
    if (!(this.can('main-meter-edit') && this.can('main-meter-delete'))) {
      this.removeActionsHeader()
    }
    this.getMeters()
    if (this.meterId) {
      this.breadcrumbs.pop()
      this.breadcrumbs.push(
        {
          text: 'Main Meters',
          disabled: false,
          to: { name: 'main-meters' },
        },
        {
          text: this.meterId,
          disabled: true,
          to: { name: 'meter-details' },
        },
      )
    }
  },
  methods: {
    getMeters: _.debounce(function () {
      this.isMetersLoading = true
      axios
        .get(
          `${this.metersUrl}?main_meters=true&sortBy=${this.sortBy[0]}&sortOrder=${this.sortOrder}&search=${this.search}&page=${this.pagination.page}`,
        )
        .then(response => {
          this.meters = response.data.data
          this.pagination.page = response.data.current_page
          this.pagination.pageCount = response.data.last_page
          this.pagination.itemsPerPage = response.data.per_page
          this.isMetersLoading = false
        })
        .catch(error => {
          this.isMetersLoading = false
          this.$notification.error(error.response.data.message)
        })
    }, 500),
    updateValveStatus(meterId, status) {
      axios
        .put(`valve-status/${meterId}`, {
          valve_status: status,
        })
        .then(() => {
          this.$notification.success('Valve status updated')
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
        })
    },
    editMeter(meter) {
      this.meter = meter
      this.showAddMeterDialog = true
    },
    showDeleteMeterDialog(id) {
      this.showConfirmDeleteDialog = true
      this.toDelete = id
    },
    deleteMeter() {
      this.isConfirmDeleteDialogButtonLoading = true
      axios
        .delete(`meters/${this.toDelete}`)
        .then(() => {
          this.$notification.success('Meter deleted successfully')
          this.isConfirmDeleteDialogButtonLoading = false
          this.showConfirmDeleteDialog = false
          this.getMeters()
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isConfirmDeleteDialogButtonLoading = false
          this.showConfirmDeleteDialog = false
        })
    },
    onMeterClick(meterId) {
      this.$router.push({ name: 'more-meter-details', params: { id: meterId } })
    },
    onPageChange() {
      this.getMeters()
    },
  },
}
</script>
